import { RegionShared } from "@1games-ru/region-shared";
import { useEffect, useLayoutEffect } from "react";
import rpc from "@1games-ru/rage-rpc";
import PlayerMpProvider from "@/core/data-provider/data-mp/player/player-mp-provider";
import useAppSelector from "@/core/helpers/useAppSelector";
import useGetPlayer from "@/shared/hooks/useGetPlayer";
import {
  ExtendedNotificationType,
  useNotificationStore,
} from "@/stores/notifications/notificationStore";
import { playerInfoSelector } from "@/domains/player/slice";

function useNotificationSubscription() {
  const { addNotification } = useNotificationStore();

  useEffect(() => {
    rpc.register(
      RegionShared.Cef.Events.Notification.NotificationSend,
      async (state: typeof RegionShared.Cef.Requests.NotificationRequest) => {
        addNotification({
          type: state.type as ExtendedNotificationType,
          data: state.data,
        });
      },
    );

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Notification.NotificationSend);
    };
  }, [addNotification]);
}

function useOfferSubscription() {
  const { getPlayer } = useGetPlayer();
  const playerInfo = useAppSelector(playerInfoSelector);
  const { addNotification, removeNotification } = useNotificationStore();

  useLayoutEffect(() => {
    if (!playerInfo) {
      getPlayer();
    }

    rpc.register(
      RegionShared.Cef.Events.Offer.OfferPending,
      async (state: typeof RegionShared.Cef.Requests.OfferPendingRequest) => {
        if (playerInfo.character.id === state.baseCharacterId) {
          return;
        }

        const character = await PlayerMpProvider.getCharacter(state.baseCharacterId);

        if (!character) {
          console.error("Character not found");
          return;
        }

        addNotification({
          id: state.uuid,
          type: ExtendedNotificationType.Offer,
          data: {
            type: state.type,
            baseCharacterId: state.baseCharacterId,
            targetCharacterId: state.targetCharacterId,
            baseCharacterName: character.name.content,
            targetId: state.targetId,
            quantity: state.quantity,
            amount: state.amount,
            payload: state.payload,
            status: state.status,
            createdAt: state.createdAt,
            destroyAt: state.destroyAt,
          },
        });
      },
    );

    rpc.register(
      RegionShared.Cef.Events.Offer.OfferResolved,
      async (state: typeof RegionShared.Cef.Requests.OfferResolvedRequest): Promise<void> => {
        removeNotification(state.uuid);
      },
    );

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Offer.OfferPending);
      rpc.unregister(RegionShared.Cef.Events.Offer.OfferResolved);
    };
  }, [addNotification, playerInfo, removeNotification]);

  return {};
}

export function useGlobalNotificationSubscription() {
  useNotificationSubscription();
  useOfferSubscription();
}
